import { useCallback, useEffect, useMemo, useState } from "react";

import { ActiveDrawerFlag, ClientFlag } from "../interfaces/client-flags";
import { PracticeFlag } from "../interfaces/flags";
import { matchesFlagSearch, normalizeFlagSearch } from "../utils/flags";

interface ClientFlagStaging {
  /** Applied flags plus everything staged, as the `Active flags` section renders them. */
  activeFlags: ActiveDrawerFlag[];
  /** Library entries neither applied nor staged, filtered by the search term. */
  availableFlags: PracticeFlag[];
  stagedFlagIds: string[];
  hasStagedChanges: boolean;
  stage: (libraryFlagId: string) => void;
  /** Takes a staged entry back out of the active list. It only changes local state. */
  unstage: (libraryFlagId: string) => void;
  reset: () => void;
}

/**
 * Holds the drawer's staged additions.
 *
 * Nothing is written until `Save`, so staging is local state that a close throws away. The drawer
 * re-seeds it whenever it opens, which is what makes closing with `X` a discard.
 *
 * @param appliedFlags - The chart's flags, as read from the server
 * @param libraryFlags - The practice's whole library
 * @param searchTerm - Filters `availableFlags` by name, case-insensitive
 * @param isOpen - Re-seeds the staging when the drawer opens
 */
export default function useClientFlagStaging(
  appliedFlags: ClientFlag[],
  libraryFlags: PracticeFlag[],
  searchTerm: string,
  isOpen: boolean,
): ClientFlagStaging {
  const [stagedFlagIds, setStagedFlagIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) setStagedFlagIds([]);
  }, [isOpen]);

  const stage = useCallback(
    (libraryFlagId: string) =>
      setStagedFlagIds((staged) =>
        staged.includes(libraryFlagId) ? staged : [...staged, libraryFlagId],
      ),
    [],
  );

  const unstage = useCallback(
    (libraryFlagId: string) =>
      setStagedFlagIds((staged) => staged.filter((stagedId) => stagedId !== libraryFlagId)),
    [],
  );

  const reset = useCallback(() => setStagedFlagIds([]), []);

  const appliedLibraryFlagIds = useMemo(
    () => new Set(appliedFlags.map(({ libraryFlagId }) => libraryFlagId)),
    [appliedFlags],
  );

  /**
   * The applied flags come from the chart read rather than from the library, because a deprecated
   * flag has no entry in the library the drawer holds — `Add a flag` is built from the *active*
   * library, which is what keeps a deleted entry from being applied again. Reading the active
   * section off that same list would drop the very flags this section has to show.
   */
  const activeFlags = useMemo<ActiveDrawerFlag[]>(() => {
    const staged = libraryFlags.reduce<ActiveDrawerFlag[]>((flags, libraryFlag) => {
      if (stagedFlagIds.includes(libraryFlag.id))
        flags.push({ state: "staged", flag: libraryFlag });

      return flags;
    }, []);

    return [...appliedFlags.map((flag) => ({ state: "applied" as const, flag })), ...staged];
  }, [appliedFlags, libraryFlags, stagedFlagIds]);

  const availableFlags = useMemo(() => {
    const normalizedSearch = normalizeFlagSearch(searchTerm);

    return libraryFlags.reduce<PracticeFlag[]>((available, libraryFlag) => {
      if (appliedLibraryFlagIds.has(libraryFlag.id) || stagedFlagIds.includes(libraryFlag.id)) {
        return available;
      }

      // The search narrows what can still be added; it never hides a flag the client carries.
      if (!matchesFlagSearch(libraryFlag, normalizedSearch)) return available;

      available.push(libraryFlag);

      return available;
    }, []);
  }, [libraryFlags, appliedLibraryFlagIds, stagedFlagIds, searchTerm]);

  return {
    activeFlags,
    availableFlags,
    stagedFlagIds,
    hasStagedChanges: stagedFlagIds.length > 0,
    stage,
    unstage,
    reset,
  };
}
